import { client } from 'util/sdk';
import { GET, POST, PUT } from 'util/api';
import { FSPS } from 'consts';

const returnModel = { name: 'palConfig' };

export const PAL_DUPLICATE_SUBDOMAIN_IN_PAYLOAD_REASON =
  'PAL_DUPLICATE_GUEST_URL_SUBDOMAIN_IN_PAYLOAD';
export const PAL_DUPLICATE_SUBDOMAIN_EXISTS_REASON =
  'PAL_DUPLICATE_GUEST_URL_SUBDOMAIN_EXISTS';

const normalizeText = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const getFailureReasonCode = (reasonText) => {
  if (/payload|import|json|file/.test(reasonText)) {
    return PAL_DUPLICATE_SUBDOMAIN_IN_PAYLOAD_REASON;
  }

  return PAL_DUPLICATE_SUBDOMAIN_EXISTS_REASON;
};

const normalizeOrThrowPalFailure = (result) => {
  const resultStatus = normalizeText(result?.result ?? result?.status);
  const reasonText = normalizeText(
    result?.reason ?? result?.resultReason ?? result?.error?.message
  );

  const isFailedResult =
    resultStatus === 'failed' || resultStatus === 'failure';
  const isDuplicateSubdomainReason =
    /duplicate/.test(reasonText) &&
    /(subdomain|guest\s*url|infrontofpin)/.test(reasonText);

  if (isFailedResult && isDuplicateSubdomainReason) {
    const reason = getFailureReasonCode(reasonText);
    const message =
      'Pay a Loan import failed: Guest URL subdomain must be unique.';

    const err = new Error(message);
    err.response = { status: 412, statusText: 'Precondition Failed' };
    err.error = { detail: reasonText, message, reason };
    throw err;
  }

  return result;
};

const toApi = ({ guestUrl, ...rest }) => ({
  ...rest,
  inFrontOfPinSubdomain: guestUrl,
});

const fromApi = (data) => {
  if (!data) return data;
  const { inFrontOfPinSubdomain, guestUrl, ...rest } = data;
  return {
    ...rest,
    guestUrl:
      inFrontOfPinSubdomain !== undefined ? inFrontOfPinSubdomain : guestUrl,
  };
};

export const loadURL = (fspId) => `/v2/${FSPS}/${fspId}/fspconfigs/pal`;
const updateURL = (fspId, id) => `${loadURL(fspId)}/${id}`;

export const load = (fspId) => {
  return client
    .executeRequest(GET, loadURL(fspId), { returnModel })
    .then((result) =>
      Array.isArray(result) ? result.map(fromApi) : fromApi(result)
    );
};

export const update = ({ fspId, id, ...params }, auth) => {
  return client
    .executeRequest(PUT, updateURL(fspId, id), {
      data: toApi(params),
      queryParams: {
        adminUserId: auth.id,
        lastUpdatedDate: params.updatedAt,
      },
      returnModel,
    })
    .then(normalizeOrThrowPalFailure)
    .then((result) =>
      Array.isArray(result) ? result.map(fromApi) : fromApi(result)
    );
};

export const buildCreatePayload = ({
  statementImportFrom,
  feeBundleOffOn,
  ssoUserOffOn,
  enrolledUserOffOn,
  guestUserOffOn,
  partialImportOffOn,
  holdAccountRouting,
  holdAccountNumber,
  holdAccountNumberSuffix,
  holdAccountType,
  feeHoldingAccountRouting,
  feeHoldingAccountNumber,
  feeHoldingAccountSuffix,
  feeHoldingAccountType,
  feeTransactOnBehalf,
  debitCardOffOn,
  creditCardOffOn,
  paymentPurposeOffOn,
  paymentPurposeCoreOffOn,
  paymentPurposeConfig,
  externalDdaOffOn,
  extDdaRtConfig,
  termsAndConditionsOffOn,
  palDefaultOffOn,
  singlePaymentFeeOffOn,
  recurringPaymentFeeOffOn,
}) => ({
  statementImportFrom,
  feeBundleOffOn,
  ssoUserOffOn,
  enrolledUserOffOn,
  guestUserOffOn,
  partialImportOffOn,
  holdAccountRouting,
  holdAccountNumber,
  holdAccountNumberSuffix,
  holdAccountType,
  feeHoldingAccountRouting,
  feeHoldingAccountNumber,
  feeHoldingAccountSuffix,
  feeHoldingAccountType,
  feeTransactOnBehalf,
  debitCardOffOn,
  creditCardOffOn,
  paymentPurposeOffOn,
  paymentPurposeCoreOffOn,
  paymentPurposeConfig,
  externalDdaOffOn,
  extDdaRtConfig,
  termsAndConditionsOffOn,
  palDefaultOffOn,
  singlePaymentFeeOffOn,
  recurringPaymentFeeOffOn,
});

export const create = ({ fspId, ...params }, auth) => {
  return client
    .executeRequest(POST, loadURL(fspId), {
      data: toApi(params),
      queryParams: {
        adminUserId: auth.id,
      },
      returnModel,
    })
    .then(normalizeOrThrowPalFailure)
    .then((result) =>
      Array.isArray(result) ? result.map(fromApi) : fromApi(result)
    );
};
